import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Message as MessageType } from '../../types';
import FollowUpSuggestions from './FollowUpSuggestions';
// import DataPlaceholder from '../DataDisplay/DataPlaceholder';
import SQLQueryDisplay from '../DataDisplay/SQLQueryDisplay';
import TableDataDisplay from '../DataDisplay/TableDataDisplay';
import SearchEnrichmentDisplay from '../DataDisplay/SearchEnrichmentDisplay';
import VisualizationDisplay from '../Charts/VisualizationDisplay';
import { CitationRenderer } from '../Citation';
import FeedbackButtons from './FeedbackButtons';
import { useChat } from '../../contexts/ChatContext';
import { logger } from '../../utils/Logger';
import { FEATURE_FLAGS } from '../../config/featureFlags';

interface MessageProps {
  message: MessageType;
}

const Message: React.FC<MessageProps> = ({ message }) => {
  const { currentConversationId, updateMessageMetadata } = useChat();
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';
  
  // Format timestamp
  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  }).format(message.timestamp);

  // Determine message styling based on role
  const messageClasses = isUser 
    ? 'bg-primary text-white ml-auto rounded-2xl shadow-sm' 
    : isSystem 
      ? '' 
      : 'bg-neutral dark:bg-dark-secondary border border-neutral-dark dark:border-dark-border rounded-xl shadow-sm'; // Placeholder for alternate assistant bubbles messages

  // Function to format content for display
  const getFormattedContent = () => {
    if (!message.content) return '';

    let contentString = '';
    if (typeof message.content === 'string') {
      contentString = message.content;
    } else if (typeof message.content === 'object') {
      const content = message.content as any;
      const summary = content.summary || '';
      const details = content.details || '';
      if (summary && details) {
        contentString = `${summary}\n\n${details}`;
      } else if (summary) {
        contentString = summary;
      } else if (details) {
        contentString = details;
      } else {
        contentString = String(message.content);
      }
    } else {
      contentString = String(message.content);
    }
    // Robustly convert all forms of escaped newlines (\\n, \\r\\n, etc.) to real line breaks for markdown rendering
    return contentString
      .replace(/\\{2,}n/g, '\n') // double or more backslashes before n
      .replace(/\\n/g, '\n')     // single-escaped
      .replace(/\r\n|\r/g, '\n'); // normalize CRLF
  };
  
  const formattedContent = getFormattedContent();
  
  // Development-only debug logging
  if (FEATURE_FLAGS.ENABLE_DEBUG_LOGGING) {
    logger.debug('Message rendering', {
      messageId: message.id,
      role: message.role,
      hasMetadata: !!message.metadata,
      hasVisualization: !!message.metadata?.visualization,
      hasTableData: !!message.metadata?.tableData,
      hasSqlQuery: !!message.metadata?.sqlQuery,
      contentLength: formattedContent.length
    });
  }
  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : isAssistant ? 'items-center w-full' : 'items-start w-full'}`}>
      {/* Message header with role indicator */}
      <div className={`text-xs text-text-tertiary dark:text-dark-textSecondary mb-1 ${isAssistant ? 'w-full sm:w-[90%] md:w-[85%] lg:w-[80%] text-left' : ''}`}>
        {isUser ? 'You' : isSystem ? 'System' : 'Shipment CoCaptain'} • {formattedTime}
      </div>
      
      {/* Message content */}
      {isUser || isSystem ? (
        <div className={`px-4 py-3 max-w-full sm:max-w-sm md:max-w-lg lg:max-w-3xl ${messageClasses}`}>
          <div className={`prose-neutral ${isUser ? 'prose-invert' : ''} max-w-none`}>
            <ReactMarkdown>{formattedContent}</ReactMarkdown>
            {/* message.content */}
          </div>
        </div>
      ) : (
        // Assistant message as centrally positioned paragraph
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] md:w-[85%] lg:w-[80%]">

              {/* Citation loading indicator */}
              {message.metadata?.citationStatus === 'pending' && (
                <div className="mt-2 flex items-center space-x-2 text-sm text-text-secondary dark:text-dark-textSecondary">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                  <span>Adding citations...</span>
                </div>
              )}
              
              <CitationRenderer 
                content={formattedContent}
                citationDescriptions={message.metadata?.citationDescriptions}
                citationMetadata={message.metadata?.citationMetadata}
                className="prose dark:prose-invert prose-neutral max-w-none prose-sm sm:prose-base" 
              />
              
              
            {/* Display SQL query, table data, or placeholder as appropriate */}
            {message.metadata && (
              <div className="mt-4">
                
                {/* Show search enrichment insights if available */}
                {message.metadata.searchEnrichment && message.metadata.searchEnrichment.length > 0 && (
                  <SearchEnrichmentDisplay 
                    insights={message.metadata.searchEnrichment} 
                    debugInfo={message.metadata.searchEnrichmentDebug}
                  />
                )}
                      
                {/* Show table data if available */}
                {message.metadata.tableData && (
                  <TableDataDisplay 
                    data={message.metadata.tableData} 
                    title={message.metadata.tableTitle}
                  />
                )}
                
                {/* Show visualization if available */}
                {message.metadata.visualization && (
                  <div className="mt-4">
                    <VisualizationDisplay visualization={message.metadata.visualization} />
                  </div>
                )}

                {/* Show SQL query if available */}
                {message.metadata.sqlQuery && (
                  <div className={message.metadata.tableData ? "mt-4" : ""}>
                  <SQLQueryDisplay query={message.metadata.sqlQuery} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Follow-up suggestions (only for assistant messages) */}
      {message.role === 'assistant' && message.followUps && message.followUps.length > 0 && (
        <div className="mt-3 w-full flex justify-center">
          <div className="w-full sm:w-[95%] md:w-[85%] lg:w-[80%]">
            <FollowUpSuggestions suggestions={message.followUps} messageId={message.id} />
          </div>
        </div>
      )}

      {/* Feedback buttons (only for completed assistant messages) */}
      {isAssistant && message.content && !message.isStreaming && (
        <div className="mt-2 w-full flex justify-center">
          <div className="w-full sm:w-[95%] md:w-[85%] lg:w-[80%]">
            <FeedbackButtons
              messageId={message.id}
              jobId={message.metadata?.jobId}
              conversationId={currentConversationId || undefined}
              hasVisualization={!!message.metadata?.visualization}
              hasSearchEnrichment={!!(message.metadata?.searchEnrichment && message.metadata.searchEnrichment.length > 0)}
              initialRating={message.metadata?.feedbackRating}
              onFeedbackSubmitted={(rating) => {
                updateMessageMetadata(message.id, { feedbackRating: rating });
              }}
            />
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Message;
